import { Button } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../../../utils/validation';
import { pulmoAcqCustomControlDataSchema } from '../../../../../../common/validation/customControls/pulmonology';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { ThirdHeader } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { Modal } from '../../../../elements/modals/Modal';
import { NullableTextField } from '../../../../fields/NullableText';
import { SteppedSliderField } from '../../../../fields/SteppedSlider';
import { AnamneseType } from './anamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface IAnamneseAcqProps {
    anamneseData: IAcq | null;
    deleteAnamneseData: () => Promise<void>;
    onClose: () => Promise<void>;
    setAnamneseData: (item: IAcq) => Promise<void>;
}

interface IAnamneseAcqState {
    anamneseData: IAcq;
    errors: ValidationErrors;
}

export interface IAcq {
    FEV1_procent_voorspeld: string;
    FEV1_voor_luchtwegverwijding: string | null;
    FEV1_voorspeld: string | null;
    hoe_ernstig_waren_uw_asmaklachten_bij_het_s_morgens_wakker_worden_gemiddeld_in_de_afgelopen_week: string;
    hoe_vaak_bent_u_per_nacht_gemiddeld_wakker_geworden_door_uw_astma_in_de_afgelopen_week: string;
    hoe_vaak_had_u_in_de_afgelopen_week_over_het_algemeen_een_piepende_ademhaling: string;
    // eslint-disable-next-line @stylistic/max-len
    hoe_veel_puffs_inhalaties_van_een_kortwerkend_luchtwegverwijdend_middel_bijv_ventolin_bricanyl_salbutamol_of_airomir_heeft_u_op_de_meeste_dagen_genomen_in_de_afgelopen_week: string;
    // eslint-disable-next-line @stylistic/max-len
    in_welke_mate_heeft_u_zich_over_het_algemeen_kortademig_gevoeld_in_de_afgelopen_week_ten_gevolge_van_uw_astma: string;
    in_welke_mate_werd_u_over_het_algemeen_in_de_afgelopen_week_door_uw_astma_beperkt_bij_uw_activiteiten: string;
    scoreAcq: number | null;
}

const questionsAnswersAcq: { answers: string[]; question: keyof IAcq }[] = [
    {
        question: 'hoe_vaak_bent_u_per_nacht_gemiddeld_wakker_geworden_door_uw_astma_in_de_afgelopen_week',
        answers: [
            'Nooit',
            'Bijna nooit',
            'Een paar keer',
            'Verscheidene keren',
            'Vaak',
            'Heel vaak',
            'Kon niet slapen vanwege astma',
        ],
    },
    {
        question: 'hoe_ernstig_waren_uw_asmaklachten_bij_het_s_morgens_wakker_worden_gemiddeld_in_de_afgelopen_week',
        answers: [
            'Geen klachten ',
            'Heel lichte klachten ',
            'Lichte klachten ',
            'Matige klachten',
            'Vrij ernstige klachten',
            'Ernstige klachten',
            'Heel ernstige klachten',
        ],
    },
    {
        question:
            'in_welke_mate_werd_u_over_het_algemeen_in_de_afgelopen_week_door_uw_astma_beperkt_bij_uw_activiteiten',
        answers: [
            'Helemaal niet beperkt',
            'Nauwelijks beperkt',
            'Een beetje beperkt',
            'Tamelijk beperkt',
            'Erg beperkt',
            'Heel erg beperkt',
            'Volledig beperkt',
        ],
    },
    {
        question:
            'in_welke_mate_heeft_u_zich_over_het_algemeen_kortademig_gevoeld_in_de_afgelopen_week_ten_gevolge_van_uw_astma',
        answers: [
            'Helemaal niet',
            'Nauwelijks',
            'Een beetje',
            'Middelmatig',
            'Vrij ernstig',
            'Ernstig',
            'Heel ernstig',
        ],
    },
    {
        question: 'hoe_vaak_had_u_in_de_afgelopen_week_over_het_algemeen_een_piepende_ademhaling',
        answers: ['Nooit', 'Zelden', 'Af en toe', 'Geregeld', 'Vaak', 'Meestal', 'Altijd'],
    },
    {
        question:
            'hoe_veel_puffs_inhalaties_van_een_kortwerkend_luchtwegverwijdend_middel_bijv_ventolin_bricanyl_salbutamol_of_airomir_heeft_u_op_de_meeste_dagen_genomen_in_de_afgelopen_week',
        answers: [
            'Geen',
            '1-2 puffs/inhalaties',
            '3-4 puffs/inhalaties',
            '5-8 puffs/inhalaties',
            '9-12 puffs/inhalaties',
            '13-16 puffs/inhalaties',
            'Meer dan 16 puffs/inhalaties',
        ],
    },
    {
        question: 'FEV1_procent_voorspeld',
        answers: ['> 95% voorspeld', '95-90%', '89-80%', '79-70%', '69-60%', '59-50%', '< 50% voorspeld'],
    },
];

export class AnamneseAcq extends Component<IAnamneseAcqProps, IAnamneseAcqState> {
    constructor(props: IAnamneseAcqProps, context?: {}) {
        super(props, context);
        this.state = {
            anamneseData: {
                hoe_vaak_bent_u_per_nacht_gemiddeld_wakker_geworden_door_uw_astma_in_de_afgelopen_week:
                    'Verscheidene keren',
                hoe_ernstig_waren_uw_asmaklachten_bij_het_s_morgens_wakker_worden_gemiddeld_in_de_afgelopen_week:
                    'Matige klachten',
                in_welke_mate_werd_u_over_het_algemeen_in_de_afgelopen_week_door_uw_astma_beperkt_bij_uw_activiteiten:
                    'Tamelijk beperkt',
                // eslint-disable-next-line @stylistic/max-len
                in_welke_mate_heeft_u_zich_over_het_algemeen_kortademig_gevoeld_in_de_afgelopen_week_ten_gevolge_van_uw_astma:
                    'Middelmatig',
                hoe_vaak_had_u_in_de_afgelopen_week_over_het_algemeen_een_piepende_ademhaling: 'Geregeld',
                // eslint-disable-next-line @stylistic/max-len
                hoe_veel_puffs_inhalaties_van_een_kortwerkend_luchtwegverwijdend_middel_bijv_ventolin_bricanyl_salbutamol_of_airomir_heeft_u_op_de_meeste_dagen_genomen_in_de_afgelopen_week:
                    '5-8 puffs/inhalaties',
                FEV1_procent_voorspeld: '79-70%',
                FEV1_voor_luchtwegverwijding: null,
                FEV1_voorspeld: null,
                scoreAcq: null,
                ...(this.props.anamneseData || {}),
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'request',
                    item: this.state.anamneseData,
                    errors: this.state.errors,
                    onChange: async (key, value) => await this.handleChange(key, value),
                    onDoValidate: async (key) => await this.validateOne(key),
                }}
            >
                <Modal
                    actionButtons={[
                        <Button
                            onClick={async () => await this.props.deleteAnamneseData()}
                            variant="outline"
                        >
                            Verwijder
                        </Button>,
                        <Button
                            onClick={async () => await this.validateAnamneseData()}
                            variant="solid"
                        >
                            Anamnese opslaan
                        </Button>,
                    ]}
                    isOpen
                    onClose={async () => await this.props.onClose()}
                    size="large"
                    title="ACQ anamnese"
                >
                    <AnamneseWrapper anamneseType={AnamneseType.ACQ}>
                        <ThirdHeader text="In de afgelopen week..." />

                        <SteppedSliderField
                            fieldKey="hoe_vaak_bent_u_per_nacht_gemiddeld_wakker_geworden_door_uw_astma_in_de_afgelopen_week"
                            label="Hoe vaak bent u per nacht gemiddeld wakker geworden door uw astma in de afgelopen week?"
                            options={this.getAcqOptions(
                                'hoe_vaak_bent_u_per_nacht_gemiddeld_wakker_geworden_door_uw_astma_in_de_afgelopen_week'
                            )}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="hoe_ernstig_waren_uw_asmaklachten_bij_het_s_morgens_wakker_worden_gemiddeld_in_de_afgelopen_week"
                            label="Hoe ernstig waren uw astmaklachten bij het 's morgens wakker worden gemiddeld in de afgelopen week?"
                            options={this.getAcqOptions(
                                'hoe_ernstig_waren_uw_asmaklachten_bij_het_s_morgens_wakker_worden_gemiddeld_in_de_afgelopen_week'
                            )}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="in_welke_mate_werd_u_over_het_algemeen_in_de_afgelopen_week_door_uw_astma_beperkt_bij_uw_activiteiten"
                            label="In welke mate werd u over het algemeen in de afgelopen week door uw astma beperkt bij uw activiteiten?"
                            options={this.getAcqOptions(
                                'in_welke_mate_werd_u_over_het_algemeen_in_de_afgelopen_week_door_uw_astma_beperkt_bij_uw_activiteiten'
                            )}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="in_welke_mate_heeft_u_zich_over_het_algemeen_kortademig_gevoeld_in_de_afgelopen_week_ten_gevolge_van_uw_astma"
                            label="In welke mate heeft u zich over het algemeen kortademig gevoeld in de afgelopen week ten gevolge van uw astma?"
                            options={this.getAcqOptions(
                                'in_welke_mate_heeft_u_zich_over_het_algemeen_kortademig_gevoeld_in_de_afgelopen_week_ten_gevolge_van_uw_astma'
                            )}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="hoe_vaak_had_u_in_de_afgelopen_week_over_het_algemeen_een_piepende_ademhaling"
                            label="Hoe vaak had u in de afgelopen week over het algemeen een piepende ademhaling?"
                            options={this.getAcqOptions(
                                'hoe_vaak_had_u_in_de_afgelopen_week_over_het_algemeen_een_piepende_ademhaling'
                            )}
                            showLabels
                        />

                        <SteppedSliderField
                            fieldKey="hoe_veel_puffs_inhalaties_van_een_kortwerkend_luchtwegverwijdend_middel_bijv_ventolin_bricanyl_salbutamol_of_airomir_heeft_u_op_de_meeste_dagen_genomen_in_de_afgelopen_week"
                            label="Hoeveel puffs/inhalaties van een kortwerkend luchtwegverwijdend middel (bijv. ventolin, bricanyl, salbutamol of airomir) heeft u op de meeste dagen genomen in de afgelopen week?"
                            options={this.getAcqOptions(
                                'hoe_veel_puffs_inhalaties_van_een_kortwerkend_luchtwegverwijdend_middel_bijv_ventolin_bricanyl_salbutamol_of_airomir_heeft_u_op_de_meeste_dagen_genomen_in_de_afgelopen_week'
                            )}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="FEV1_procent_voorspeld"
                            label="FEV1% voorspeld"
                            options={this.getAcqOptions('FEV1_procent_voorspeld')}
                            showLabels
                        />

                        <NullableTextField
                            error={this.state.errors.has('FEV1_voor_luchtwegverwijding') ? 'fev1' : undefined}
                            fieldKey="FEV1_voor_luchtwegverwijding"
                            label="FEV1 voor luchtwegverwijding"
                            placeholder=""
                        />
                        <NullableTextField
                            error={this.state.errors.has('FEV1_voorspeld') ? 'fev1' : undefined}
                            fieldKey="FEV1_voorspeld"
                            label="FEV1 voorspeld"
                            placeholder=""
                        />
                    </AnamneseWrapper>
                </Modal>
            </FormContextProvider>
        );
    }

    private async handleChange<K extends keyof IAcq>(name: K, value: IAcq[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof IAcq) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(pulmoAcqCustomControlDataSchema, anamneseData, errors, name),
        }));
    }

    private getAcqOptions(questionName: string) {
        const questions = questionsAnswersAcq.find((questionAcq) => questionAcq.question === questionName);
        if (questions) {
            return questions.answers.map((answerAcq) => answerAcq);
        } else {
            throw new Error('No ACQ options found for question');
        }
    }

    private async validateAnamneseData() {
        await this.setState(({ anamneseData }) => ({
            anamneseData: { ...anamneseData, scoreAcq: this.getAcqScore(anamneseData) },
        }));
        const { hasErrors, errors, item } = validate(pulmoAcqCustomControlDataSchema, this.state.anamneseData);
        await this.setState({ errors });
        if (!hasErrors) {
            this.props.setAnamneseData(item as IAcq);
        }
    }

    private getAcqScore(anamneseData: IAcq) {
        let scoreAcq = null;
        if (anamneseData) {
            for (const { question, answers } of questionsAnswersAcq) {
                const answer = answers.findIndex((answerAcq) => answerAcq === anamneseData[question]);
                if (answer !== -1) {
                    scoreAcq = scoreAcq !== null ? scoreAcq + answer : answer;
                } else {
                    throw new Error('Answer not found');
                }
            }
        }

        return scoreAcq !== null ? Number((scoreAcq / questionsAnswersAcq.length).toFixed(2)) : null;
    }
}
